import { useSyncExternalStore } from 'react';

/**
 * Tema da aplicação (at-130, 23/10/2026): claro, escuro ou seguir o sistema operacional.
 *
 * É escolha de CADA usuário, no menu lateral, gravada no servidor (`users.tema_preferido`) para
 * valer em qualquer computador. O valor também fica neste navegador para a tela já abrir com o
 * tema certo (sem piscar claro antes de a resposta chegar).
 *
 * Como o escuro funciona: as cores das telas viraram `var(--tema-x, #cor-original)`. Os tokens só
 * são definidos quando `<html data-tema="escuro">` — no claro, cada cor cai no valor original, e o
 * tema claro fica idêntico ao de antes por construção. Ver `tema.css`.
 *
 * ⚠️ Telas do associado, do fornecedor e o login ficam SEMPRE claras (`forcarClaro`): quem as abre
 * não é usuário do sistema.
 */
export type TemaConfigurado = 'CLARO' | 'ESCURO' | 'SISTEMA';
export type TemaResolvido = 'claro' | 'escuro';

const CHAVE = 'soac-tema-da-aplicacao';
const TEMAS: TemaConfigurado[] = ['CLARO', 'ESCURO', 'SISTEMA'];

let configurado: TemaConfigurado = 'CLARO';
let claroForcado = false;
let resolvido: TemaResolvido = 'claro';
const ouvintes = new Set<() => void>();

function midiaEscura(): MediaQueryList | null {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return null;
  return window.matchMedia('(prefers-color-scheme: dark)');
}

export function resolverTema(tema: TemaConfigurado): TemaResolvido {
  if (tema === 'ESCURO') return 'escuro';
  if (tema === 'SISTEMA') return midiaEscura()?.matches ? 'escuro' : 'claro';
  return 'claro';
}

function aplicarNoDocumento() {
  const proximo: TemaResolvido = claroForcado ? 'claro' : resolverTema(configurado);
  const raiz = document.documentElement;
  if (proximo === 'escuro') raiz.setAttribute('data-tema', 'escuro');
  else raiz.removeAttribute('data-tema');
  raiz.style.colorScheme = proximo === 'escuro' ? 'dark' : 'light';
  if (proximo !== resolvido) {
    resolvido = proximo;
    ouvintes.forEach((f) => f());
  }
}

/** Grava a escolha (vinda do servidor ou da tela de Administração) e aplica. */
export function definirTemaConfigurado(tema: TemaConfigurado) {
  configurado = TEMAS.includes(tema) ? tema : 'CLARO';
  try {
    window.localStorage.setItem(CHAVE, configurado);
  } catch {
    // Sem armazenamento: o tema vale nesta aba e volta a ser buscado no próximo carregamento.
  }
  aplicarNoDocumento();
  ouvintes.forEach((f) => f());
}

export function obterTemaConfigurado(): TemaConfigurado {
  return configurado;
}

/** Login e telas públicas: sempre claro, qualquer que seja a configuração. */
export function forcarClaro(forcar: boolean) {
  if (claroForcado === forcar) return;
  claroForcado = forcar;
  aplicarNoDocumento();
}

/** Chamada uma vez, antes do primeiro render: aplica o último tema conhecido neste navegador. */
export function iniciarTema() {
  let salvo: string | null = null;
  try {
    salvo = window.localStorage.getItem(CHAVE);
  } catch {
    // Sem armazenamento: começa claro, e o servidor corrige ao carregar.
  }
  configurado = TEMAS.includes(salvo as TemaConfigurado) ? (salvo as TemaConfigurado) : 'CLARO';
  // "Seguir o sistema": a troca do sistema operacional vale na hora, sem recarregar.
  midiaEscura()?.addEventListener?.('change', () => {
    if (configurado === 'SISTEMA') aplicarNoDocumento();
  });
  aplicarNoDocumento();
}

function inscrever(f: () => void) {
  ouvintes.add(f);
  return () => {
    ouvintes.delete(f);
  };
}

/** O tema escolhido (CLARO, ESCURO ou SISTEMA) — para o seletor do menu lateral. */
export function useTemaConfigurado(): TemaConfigurado {
  return useSyncExternalStore(inscrever, () => configurado, () => 'CLARO');
}

/** O tema que está sendo exibido agora (para quem desenha algo fora do CSS, como o telão). */
export function useTemaResolvido(): TemaResolvido {
  return useSyncExternalStore(inscrever, () => resolvido, () => 'claro');
}
